/**
 * Tab cleanup utility for managing browser tabs
 * Helps prevent multiple ParkPro tabs from accumulating
 */

// Store references to opened tabs for cleanup
let openedTabs = new Set();

/**
 * Track a newly opened tab
 * @param {Window} tabWindow - The window object of the opened tab
 * @param {string} purpose - The purpose of the tab (e.g., 'pdf-download', 'preview')
 */
export const trackTab = (tabWindow, purpose = 'unknown') => {
  if (tabWindow) {
    openedTabs.add({
      window: tabWindow,
      purpose,
      openedAt: Date.now()
    });
  }
};

/**
 * Close a specific tab
 * @param {Window} tabWindow - The window object to close
 */
export const closeTab = (tabWindow) => {
  try {
    if (tabWindow && !tabWindow.closed) {
      tabWindow.close();
    }
  } catch (e) {
    // Silently fail if we can't close the window
    console.log('Could not close tab:', e.message);
  }
};

/**
 * Close all tracked tabs
 */
export const closeAllTabs = () => {
  openedTabs.forEach(tab => {
    closeTab(tab.window);
  });
  openedTabs.clear();
};

/**
 * Close tabs older than specified time
 * @param {number} maxAge - Maximum age in milliseconds (default: 5 minutes)
 */
export const closeOldTabs = (maxAge = 5 * 60 * 1000) => {
  const now = Date.now();
  const tabsToClose = [];
  
  openedTabs.forEach(tab => {
    if (now - tab.openedAt > maxAge) {
      tabsToClose.push(tab);
    }
  });
  
  tabsToClose.forEach(tab => {
    closeTab(tab.window);
    openedTabs.delete(tab);
  });
};

/**
 * Clean up tabs when navigating to dashboard/home
 * This should be called when user navigates back to main areas
 */
export const cleanupOnNavigation = () => {
  // Close tabs that are older than 2 minutes
  closeOldTabs(2 * 60 * 1000);
  
  // If we have more than 3 tabs open, close the oldest ones
  if (openedTabs.size > 3) {
    const sortedTabs = Array.from(openedTabs).sort((a, b) => a.openedAt - b.openedAt);
    const tabsToClose = sortedTabs.slice(0, openedTabs.size - 3);
    
    tabsToClose.forEach(tab => {
      closeTab(tab.window);
      openedTabs.delete(tab);
    });
  }
};

/**
 * Get count of currently tracked tabs
 * @returns {number} Number of tracked tabs
 */
export const getTabCount = () => {
  return openedTabs.size;
};

/**
 * Clear all tab references (useful for cleanup)
 */
export const clearTabTracking = () => {
  openedTabs.clear();
};

// Auto-cleanup old tabs every 5 minutes
setInterval(() => {
  closeOldTabs();
}, 5 * 60 * 1000);
